import {CollaborationAttachments} from "./CollaborationAttachments";
import { CustomerError } from "../../ui/CustomerError";
import { CardGlass, GlassChip, BlobCtaButton, Typography } from "@cupcodev/ui";
import { EmptyState, Facts, FilterTabs, NaveIcon, Notice, PageHeader, PageSkeleton, Panel, RowIcon, RowLink, RowList, SectionSkeleton, type ChipTone } from "../../ui/NavePage";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type { CustomerApprovalAction, CustomerApprovalDetail, CustomerApprovalsList } from "../../read-models/approvals/customer-approval-read-model";
import { useI18n } from "../i18n/I18nProvider";
import { useScenario } from "../scenario/ScenarioProvider";
import { ApprovalFeedbackPanel } from "./CollaborationExperiencePages";

type QueryError = Readonly<{ code: string }>;
type QueryResult<T> = Readonly<{ ok: true; value: T }> | Readonly<{ ok: false; error: QueryError }>;
type LoadState<T> = Readonly<{ kind: "loading" }> | Readonly<{ kind: "ready"; value: T }> | Readonly<{ kind: "error"; code: string }>;

const useApprovalLoad = <T,>(loadKey: string, load: () => Promise<QueryResult<T>>): LoadState<T> => {
  const [state, setState] = useState<LoadState<T>>({ kind: "loading" });
  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    void load().then((result) => {
      if (cancelled) return;
      setState(result.ok ? { kind: "ready", value: result.value } : { kind: "error", code: result.error.code });
    });
    return () => { cancelled = true; };
  }, [loadKey]);
  return state;
};

const ApprovalLoading = ({ label }: { label: string }) => <PageSkeleton label={label} layout="detail" />;

const ApprovalError = ({ code, inline = false }: { code: string; inline?: boolean }) => {
  const { t } = useI18n();
  const key = code === "FORBIDDEN"
    ? "errors.forbidden"
    : code.startsWith("DEPENDENCY_")
      ? "errors.unavailable"
      : "errors.notFound";
  return <CustomerError inline={inline} title={t(`${key}.title`)} description={t(`${key}.body`)} />;
};

const approvalStatus = (state: string, t: (key: string) => string): string => {
  const key = `approvals.status.${state}`;
  const translated = t(key);
  return translated === key ? t("status.unknown") : translated;
};

const subjectLabel = (
  item: Readonly<{ subjectKey?: string; subjectText?: string }>,
  t: (key: string) => string,
): string => item.subjectText ?? (item.subjectKey === undefined ? t("approvals.untitled") : t(item.subjectKey));

const approvalTone = (state: string): ChipTone => state === "WAITING_CLIENT" || state === "READY" ? "purple" : state === "APPROVED" ? "success" : state === "REJECTED" ? "destructive" : "neutral";

type ApprovalFilter = "pending" | "all";

export const ApprovalsPage = () => {
  const { runtime, activeOrganizationId } = useScenario();
  const { t, formatDate } = useI18n();
  const [filter, setFilter] = useState<ApprovalFilter>("pending");
  const state = useApprovalLoad<CustomerApprovalsList>(
    `${runtime.scenario.id}:${activeOrganizationId.value}:approvals`,
    () => runtime.experience.listApprovals(),
  );
  const items = state.kind === "ready" ? state.value.items : [];
  const pending = items.filter((item) => item.canRespond);
  const visible = filter === "pending" ? pending : items;
  return <div className="nave-page">
    <PageHeader
      icon="lucide:badge-check"
      title={t("approvals.title")}
      description={t("approvals.subtitle")}
    />
    {state.kind === "ready" && items.length > 0 ? <div className="nave-toolbar"><FilterTabs<ApprovalFilter> label={t("layout.filter")} value={filter} onChange={setFilter} options={[{ value: "pending", label: t("layout.filter.pending"), count: pending.length }, { value: "all", label: t("layout.filter.all"), count: items.length }]} /></div> : null}
    {state.kind === "loading" ? <SectionSkeleton label={t("state.loading")} /> : state.kind === "error" ? <ApprovalError inline code={state.code} /> : items.length === 0 ? <EmptyState icon="lucide:badge-check">{t("approvals.empty")}</EmptyState> : <CardGlass variant="glass-md" elevation={0} className="nave-panel-card">
      {visible.length === 0 ? <EmptyState icon="lucide:circle-check">{t("home.attention.empty")}</EmptyState> : <RowList label={t("approvals.title")}>{visible.map((item) => <li key={item.approvalId}><RowLink
        href={`/approvals/${item.approvalId}`}
        leading={<RowIcon name="lucide:badge-check" tone={item.canRespond ? "alert" : item.state === "APPROVED" ? "success" : "neutral"} />}
        title={subjectLabel(item, t)}
        subtitle={t("approvals.version", { version: item.versionNumber })}
        meta={<>{item.requestedAt ? <span className="nave-meta-item"><NaveIcon name="lucide:calendar" />{t("approvals.requestedAt")}: {formatDate(item.requestedAt)}</span> : null}{item.completedAt ? <span className="nave-meta-item"><NaveIcon name="lucide:calendar-check" />{t("approvals.completedAt")}: {formatDate(item.completedAt)}</span> : null}</>}
        trailing={<GlassChip variant={approvalTone(item.state)}>{approvalStatus(item.state, t)}</GlassChip>}
      /></li>)}</RowList>}
    </CardGlass>}
  </div>;
};

export const ApprovalDetailPage = () => {
  const { approvalId = "" } = useParams();
  const { runtime, activeOrganizationId } = useScenario();
  const { t, formatDate } = useI18n();
  const [reload, setReload] = useState(0);
  const [mutation, setMutation] = useState<Readonly<{ kind: "idle" | "submitting" | "success" | "error"; code?: string }>>({ kind: "idle" });
  const [comment, setComment] = useState("");
  const retryToken = useRef<Readonly<{ fingerprint: string; idempotencyKey: string; correlationId: string }> | undefined>(undefined);
  const commentRef = useRef<HTMLTextAreaElement | null>(null);
  const state = useApprovalLoad<CustomerApprovalDetail>(
    `${runtime.scenario.id}:${activeOrganizationId.value}:approval:${approvalId}:${reload}`,
    () => runtime.experience.getApproval(approvalId),
  );

  const respond = (decision: CustomerApprovalAction, detail: CustomerApprovalDetail): void => {
    if (mutation.kind === "submitting") return;
    if(decision!=="APPROVE"&&!comment.trim()){setMutation({kind:"error",code:"VALIDATION_ERROR"});commentRef.current?.focus();return;}
    setMutation({ kind: "submitting" });
    const normalizedComment = comment.trim();
    const fingerprint = `${detail.approvalId}:${detail.approvalVersionId}:${decision}:${normalizedComment}`;
    let token = retryToken.current;
    if (token === undefined || token.fingerprint !== fingerprint) {
      const nonce = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      token = {
        fingerprint,
        idempotencyKey: `approval-${detail.approvalId}-${detail.approvalVersionId}-${decision}-${nonce}`,
        correlationId: `approval-ui-${nonce}`,
      };
      retryToken.current = token;
    }
    void runtime.experience.respondApproval({
      approvalId: detail.approvalId,
      approvalVersionId: detail.approvalVersionId,
      ...(detail.responseRevision===undefined?{}:{expectedRevision:detail.responseRevision}),
      decision,
      ...(normalizedComment.length === 0 ? {} : { comment: normalizedComment }),
      idempotencyKey: token.idempotencyKey,
      correlationId: token.correlationId,
      clientApplicationId: "nave-web",
    }).then((result) => {
      if (!result.ok) {
        setMutation({ kind: "error", code: result.error.code });
        // Keep the same token only for an ambiguous dependency failure so an explicit retry remains idempotent.
        if (result.error.code !== "DEPENDENCY_UNAVAILABLE") retryToken.current = undefined;
        if (result.error.code === "CONFLICT") setReload((value) => value + 1);
        return;
      }
      retryToken.current = undefined;
      setComment("");
      setMutation({ kind: "success" });
      setReload((value) => value + 1);
    });
  };

  if (state.kind === "loading") return <ApprovalLoading label={t("state.loading")} />;
  if (state.kind === "error") return <ApprovalError code={state.code} />;
  const detail = state.value;
  const busy = mutation.kind === "submitting";
  const missingReason = mutation.kind === "error" && mutation.code === "VALIDATION_ERROR" && !comment.trim();
  const title = subjectLabel(detail, t);
  return <div className="nave-page">
    <PageHeader
      crumbs={[{ label: t("approvals.title"), to: "/approvals" }, { label: title }]}
      icon="lucide:badge-check"
      title={title}
      description={detail.description}
      status={{ label: approvalStatus(detail.state, t), tone: approvalTone(detail.state) }}
      meta={<>
        <span className="nave-meta-item"><NaveIcon name="lucide:git-commit-horizontal" />{t("approvals.version", { version: detail.versionNumber })}</span>
        {detail.requestedAt ? <span className="nave-meta-item"><NaveIcon name="lucide:calendar" />{t("approvals.requestedAt")}: {formatDate(detail.requestedAt)}</span> : null}
        {detail.dueAt ? <span className="nave-meta-item"><NaveIcon name="lucide:calendar-clock" />{t("approvals.deadline")}: {formatDate(detail.dueAt)}</span> : null}
      </>}
    />
    <div className="nave-layout nave-layout-priority-aside">
      <div className="nave-layout-main">
        <Panel icon="lucide:eye" title={t("approvals.currentVersion")}>
          {detail.contentText ? <p className="nave-prose">{detail.contentText}</p> : null}
          <Facts columns={2} items={[
            detail.artifactVersion ? { label: t("approvals.artifactVersion"), value: detail.artifactVersion } : null,
            detail.completedAt ? { label: t("approvals.completedAt"), value: formatDate(detail.completedAt) } : null,
          ]} />
          {detail.attachments ? <CollaborationAttachments kind="APPROVAL" id={detail.approvalId} versionId={detail.approvalVersionId} attachments={detail.attachments} canUpload={detail.availableActions.length>0} onChanged={()=>setReload(value=>value+1)}/> : null}
        </Panel>
        <ApprovalFeedbackPanel subjectRef={detail.approvalId} />
      </div>
      <aside className="nave-layout-aside" aria-label={t("layout.context")}>
        <Panel icon="lucide:gavel" title={t("approvals.actionsLabel")}>
          {detail.availableActions.length === 0 ? <EmptyState icon="lucide:lock">{t(detail.ownership === "EXTERNAL_OWNED" ? "approvals.externalOwnerReadOnly" : "approvals.readOnly")}</EmptyState> : <div className="nave-form" aria-label={t("approvals.actionsLabel")}>
            <Notice>{t("approvals.actionVersionNotice", { version: detail.versionNumber })}</Notice>
            <label className="nave-field"><span>{t("approvals.commentOptional")}</span><textarea ref={commentRef} value={comment} onChange={(event: { target: { value: string } }) => setComment(event.target.value)} rows={4} disabled={busy} aria-describedby="approval-reason-hint" aria-invalid={missingReason || undefined} /><span id="approval-reason-hint" className="nave-field-hint">{t("approvals.reasonRequired")}</span></label>
            <div className="nave-actions-col">
              {detail.availableActions.includes("APPROVE") ? <BlobCtaButton type="button" disabled={busy} loading={busy} onClick={() => respond("APPROVE", detail)}>{t("approvals.approve")}</BlobCtaButton> : null}
              {detail.availableActions.includes("REQUEST_CHANGES") ? <BlobCtaButton type="button" variant="secondary" disabled={busy} onClick={() => respond("REQUEST_CHANGES", detail)}>{t("approvals.requestChanges")}</BlobCtaButton> : null}
              {detail.availableActions.includes("REJECT") ? <BlobCtaButton type="button" variant="original-pink" disabled={busy} onClick={() => respond("REJECT", detail)}>{t("approvals.reject")}</BlobCtaButton> : null}
            </div>
            {mutation.kind === "error" ? <Notice tone="warning">{t(mutation.code === "VALIDATION_ERROR" ? "approvals.reasonMissing" : mutation.code === "CONFLICT" ? "approvals.stale" : mutation.code === "OWNER_MUTATION_UNAVAILABLE" ? "approvals.ownerUnavailable" : "approvals.actionError")}</Notice> : null}
            <div aria-live="polite" className="nave-status-line">
              {mutation.kind === "submitting" ? t("approvals.submitting") : mutation.kind === "success" ? t("approvals.success") : ""}
            </div>
          </div>}
        </Panel>
        {detail.history.length > 0 ? <Panel icon="lucide:history" title={t("approvals.history")} tone="glass-sm"><ol className="nave-timeline">{detail.history.map((item, index) => <li key={`${item.type}:${item.approvalVersionId ?? "approval"}:${item.occurredAt}:${index}`}><span className="nave-timeline-dot" aria-hidden="true"><NaveIcon name={item.type === "APPROVED" ? "lucide:check" : item.type === "REJECTED" ? "lucide:x" : item.type === "CHANGES_REQUESTED" ? "lucide:pencil" : "lucide:upload"} /></span><div className="nave-timeline-body"><p className="nave-timeline-title">{t(`approvals.history.${item.type}`)}</p><span className="nave-timeline-time">{formatDate(item.occurredAt)}{item.versionNumber === undefined ? "" : ` · ${t("approvals.version", { version: item.versionNumber })}`}</span>{item.comment ? <Typography as="p" variant="body-sm" tone="muted">{item.comment}</Typography> : null}</div></li>)}</ol></Panel> : null}
        {detail.evidence.length > 0 ? <Panel icon="lucide:shield-check" title={t("approvals.evidence")} tone="glass-sm"><ol className="nave-timeline">{detail.evidence.map((item, index) => <li key={`${item.approvalVersionId}:${item.decidedAt}:${index}`}><span className="nave-timeline-dot" aria-hidden="true"><NaveIcon name="lucide:shield-check" /></span><div className="nave-timeline-body"><p className="nave-timeline-title">{t(`approvals.history.${item.outcome}`)}</p><span className="nave-timeline-time">{t("approvals.version", { version: item.versionNumber })} · {formatDate(item.decidedAt)}</span>{item.comment ? <Typography as="p" variant="body-sm" tone="muted">{item.comment}</Typography> : null}</div></li>)}</ol></Panel> : null}
      </aside>
    </div>
  </div>;
};
